import { FC, useEffect, useMemo, useState } from 'react';
import { useProgressBar } from '@hooks/helpers/useProgressBar';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { useNavigate } from 'react-router-dom';
import FirstStepLinkBank from '@sections/link_card_bank/link_bank/step1';
import SecondStepInstantLinkBank from '@sections/link_card_bank/link_bank/step2_instant';
import ThirdStepLinkBank from '@sections/link_card_bank/link_bank/step3';
import SecondStepManualLinkBank from '@sections/link_card_bank/link_bank/step2_manual';
import { routes } from '@const/routes';
import { useForm } from '@hooks/helpers/useForm';
import { defaultLinkBank } from '@const/default_form_data';
import { zodResolver } from '@hookform/resolvers/zod';
import { linkBankValidation } from '@helpers/validation_objects/link_card_or_bank';
import { FormProvider } from 'react-hook-form';
import { useLocales } from '@hooks/helpers/useLocales';
import { apiAccountService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useTranslation } from 'react-i18next';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useQueryParams } from '@hooks/helpers/useQueryParams';

const steps = (t: (val: string) => string) => [
    {
        title: t('methods'),
        id: '1'
    },
    {
        title: t('verification'),
        id: '2'
    },
    {
        title: t('complete'),
        id: '3'
    }
];

interface IProps {
    handleChoseOtherType: () => void;
    onChangeStep: (val: string) => void;
    activeStep: string;
}

const LinkBankSection: FC<IProps> = ({ handleChoseOtherType, onChangeStep, activeStep }) => {
    const navigate = useNavigate();
    const { validationLocale, submitLocale, requestErrorLocale, requestSuccessLocale } =
        useLocales();
    const { t } = useTranslation('sections', {
        keyPrefix: 'link_card_bank.link_bank'
    });
    const [isLoading, setIsLoading] = useState(false);
    const stepsMemo = useMemo(() => [...steps(t), undefined], [steps]);
    const { currentStep, nextStep, changeStep } = useProgressBar(stepsMemo);
    const [linkMethod, setLinkMethod] = useState<'manual' | 'instant'>('instant');
    const [preventLinkMethod, setPreventLinkMethod] = useState<'manual' | 'instant'>('instant');
    const [isDone, setIsDone] = useState(false);
    const { user, refetchAccount } = useUserContext();
    const [loginId, accountId] = useQueryParams(['loginId', 'accountId']);
    const formMethods = useForm({
        defaultValues: defaultLinkBank,
        resolver: zodResolver(
            linkBankValidation({
                method: linkMethod,
                messages: {
                    account_number: validationLocale('account_number'),
                    transit_number: validationLocale('transit_number'),
                    account_name: validationLocale('account_name'),
                    bank_number: validationLocale('bank_number')
                }
            })
        )
    });

    useEffect(() => {
        onChangeStep(currentStep?.id || '1');
    }, [currentStep]);

    useEffect(() => {
        if (activeStep !== currentStep?.id) {
            changeStep(activeStep);
        }
    }, [activeStep]);

    useEffect(() => {
        if (loginId && user?.id && !isLoading && accountId) {
            changeStep('3');
            setIsLoading(true);
            apiAccountService
                .flinksConnect(loginId, accountId)
                .then(() => {
                    refetchAccount();
                    changeStep('3');
                    formMethods.reset(defaultLinkBank);
                })
                .catch((e) => {
                    changeStep('1');
                    requestErrorLocale(e);
                })
                .finally(() => setIsLoading(false));
        }
    }, [user, loginId, accountId]);

    const renderStep = () => {
        switch (currentStep?.id) {
            case '1':
                return (
                    <FirstStepLinkBank
                        handleChoseOtherType={handleChoseOtherType}
                        type={preventLinkMethod}
                        onChange={(value) => setPreventLinkMethod(value)}
                    />
                );
            case '2':
                return linkMethod === 'manual' ? (
                    <SecondStepManualLinkBank />
                ) : (
                    <SecondStepInstantLinkBank />
                );
            case '3':
                return (
                    <ThirdStepLinkBank
                        isManual={linkMethod === 'manual'}
                        changeStep={changeStep}
                        isLoading={isLoading}
                    />
                );
        }
    };

    const { handleRequest: handleLinkBankAccount, isLoading: isLinkBankLoading } = useApiMutation({
        method: () => {
            const { account_number, transit_number, account_name, bank_number } =
                formMethods.getValues();
            return apiAccountService.linkBankAccount({
                account_name,
                transit_number: Number(transit_number),
                account_number: account_number,
                bank_number: Number(bank_number)
            });
        },
        onSuccess: () => {
            requestSuccessLocale('link_bank_account');
            refetchAccount();
            nextStep();
            setIsDone(true);
        },
        onError: requestErrorLocale
    });

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <FormProvider {...formMethods}>
                <div className={styles.content}>{renderStep()}</div>
                {((linkMethod !== 'instant' && currentStep?.id !== '2') ||
                    currentStep?.id === '1' ||
                    linkMethod === 'manual') &&
                    !(linkMethod === 'manual' && currentStep?.id === '3') && (
                        <Button
                            variant="primary"
                            isLoading={isLinkBankLoading}
                            onClick={() => {
                                if (currentStep?.id === '1') {
                                    setLinkMethod(preventLinkMethod);
                                    nextStep();
                                } else if (currentStep?.id === '2') {
                                    formMethods.trigger().then((isValid) => {
                                        if (isValid) {
                                            handleLinkBankAccount(undefined);
                                        }
                                    });
                                } else {
                                    navigate(routes.dashboard);
                                }
                            }}>
                            {currentStep?.id === '2'
                                ? 'Link a bank account'
                                : isDone
                                  ? submitLocale('done')
                                  : submitLocale('next')}
                        </Button>
                    )}
            </FormProvider>
        </motion.div>
    );
};

export default LinkBankSection;
